import * as Phaser from "phaser-ce"
import { ActivityKey } from "../../../../shared/models/ActivityKey"
import { Buttons } from "../../../generic-sprites/Buttons"
import { loadGenericAssets } from "../../../generic-sprites/GenericAssets"
import { VapeSprite } from "../../../generic-sprites/VapeSprite"
import { AudioSync } from "../../../utils/AudioSync"
import { CameraZoom } from "../../../utils/CameraZoom"
import { PhaserUtils } from "../../../utils/PhaserUtils"
import { Scale } from "../../../utils/Scale"
import { Sprites } from "../../../utils/Sprites"
import { SecondHandRepo } from "../SecondHandRepo"
import { loadSharedAssets, SharedSecondHandAssets } from "../SharedSecondHandAssets"
import { loadAssets, VaporAssets } from "./VaporAssets"
import { VapPos } from "./VapPos"

export class VaporState extends Phaser.State {

    preload() {
        loadAssets(this.game)
        loadSharedAssets(this.game)
        loadGenericAssets(this.game)
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)

        const boxScale = 0.8
        const roomScale = 0.65

        const boxBack = this.game.add.image(0, 0, SharedSecondHandAssets.BOX_BACK)
        Scale.scaleSprite(boxBack, boxScale)
        Sprites.centerInWorld(boxBack)

        const room = this.game.add.image(VapPos.roomX(), VapPos.roomY(), VaporAssets.ROOM)
        Scale.scaleSprite(room, roomScale)

        const babyCrib = this.game.add.sprite(VapPos.babyCribX(), VapPos.babyCribY(), VaporAssets.BABY_CRIB)
        Scale.scaleSprite(babyCrib, 0.51)

        const secretBabyButton = this.game.add.sprite(VapPos.babyButtonX(), VapPos.babyButtonY())
        secretBabyButton.width = Scale.width() * 0.15
        secretBabyButton.height = Scale.height() * 0.18

        const dog = this.game.add.sprite(VapPos.dogX(), VapPos.dogY(), VaporAssets.DOG)
        Scale.scaleSprite(dog, roomScale)
        const dogLickAnim = dog.animations.add("dogLick", [1, 2, 3, 4, 5, 6, 5, 4, 3, 2])
        const dogVomitAnim = dog.animations.add("dogVomit", [7, 8, 9, 10, 11, 12])

        const vaporPuddle = this.game.add.image(VapPos.vaporPuddleX(), VapPos.vaporPuddleY(), VaporAssets.VAPOR_PUDDLE)
        Scale.scaleSprite(vaporPuddle, roomScale)
        vaporPuddle.alpha = 0

        const momVapeSmoke = new VapeSprite(this.game, VapPos.momVaporX(), VapPos.momVaporY())

        const roomVapor = this.game.add.image(VapPos.roomVaporX(), VapPos.roomVaporY(), VaporAssets.VAPE_CLOUD)
        Scale.scaleSprite(roomVapor, 1)
        roomVapor.alpha = 0
        const roomVapor2 = this.game.add.image(VapPos.roomVapor2X(), VapPos.roomVapor2Y(), VaporAssets.VAPE_CLOUD)
        Scale.scaleSprite(roomVapor2, 0.6)
        roomVapor2.alpha = 0
        const roomVapor3 = this.game.add.image(VapPos.roomVapor3X(), VapPos.roomVapor3Y(), VaporAssets.VAPE_CLOUD)
        Scale.scaleSprite(roomVapor3, 0.5)
        roomVapor3.alpha = 0
        const roomVapor4 = this.game.add.image(VapPos.roomVapor4X(), VapPos.roomVapor4Y(), VaporAssets.VAPE_CLOUD)
        Scale.scaleSprite(roomVapor4, 0.4)
        roomVapor4.alpha = 0

        const boxFront = this.game.add.image(0, 0, SharedSecondHandAssets.BOX_FRONT)
        Scale.scaleSprite(boxFront, boxScale)
        Sprites.centerInWorld(boxFront)

        const intro = this.game.add.audio(VaporAssets.INTRO)

        const oneOffs = new AudioSync(this.game, intro)
        oneOffs.add(1000, () => {
            momVapeSmoke.vapeLoop()
        })
        oneOffs.add(5000, () => {
            const cloudVisibility = 0.8
            Sprites.fade(roomVapor, {fadeDuration: 1000, fadeAmount: cloudVisibility})
            Sprites.fade(roomVapor2, {fadeDuration: 1000, fadeAmount: cloudVisibility})
            Sprites.fade(roomVapor3, {fadeDuration: 1000, fadeAmount: cloudVisibility})
            Sprites.fade(roomVapor4, {fadeDuration: 1000, fadeAmount: cloudVisibility})
        })
        oneOffs.add(11000, () => {
            CameraZoom.moveSprite(roomVapor, null, VapPos.roomVaporY2(), 2000)
            CameraZoom.moveSprite(roomVapor2, null, VapPos.roomVapor2Y2(), 2000)
            CameraZoom.moveSprite(roomVapor3, null, VapPos.roomVapor3Y2(), 2000)
            CameraZoom.moveSprite(roomVapor4, null, VapPos.roomVapor4Y2(), 2000)
        })
        oneOffs.add(13000, () => {
            Sprites.fade(vaporPuddle, {fadeDuration: 1000})
        })

        oneOffs.start()

        intro.play()

        const onIntroDone = () => {
            Buttons.makeSpriteButton(dog)
            dog.events.onInputDown.addOnce(onDogClicked)
        }
        intro.onStop.add(onIntroDone)

        const clickBaby = this.game.add.audio(VaporAssets.CLICK_BABY)

        const onDogClicked = () => {
            Buttons.undoSpriteButton(dog)
            const dogOneOffs = this.game.time.create()
            dogOneOffs.add(500, () => {
                let timesLicked = 0
                dogLickAnim.onComplete.add(() => {
                    timesLicked++
                    if (timesLicked < 3) {
                        dogLickAnim.play(5)
                    } else {
                        dogVomitAnim.play(3)
                    }
                })
                dogLickAnim.play(5)
            })

            dogOneOffs.add(8000, () => {
                clickBaby.play()
            })
            dogOneOffs.start()
        }

        const babyCoughing = this.game.add.audio(VaporAssets.BABY_COUGH)

        clickBaby.onStop.addOnce(() => {
            Buttons.makeSpriteButton(secretBabyButton)
            secretBabyButton.events.onInputDown.addOnce(onBabyClicked)
        })

        const onBabyClicked = () => {
            Buttons.undoSpriteButton(secretBabyButton)
            babyCoughing.play()
        }

        babyCoughing.onPlay.addOnce(() => {
            babyCrib.frame = 1
        })

        babyCoughing.onStop.addOnce(() => {
            babyCrib.frame = 0
            const babyOneOffs = this.game.time.create()
            babyOneOffs.add(2000, () => {
                SecondHandRepo.instance.setVaporAsComplete()
                this.game.state.start(ActivityKey.SECOND_HAND)
            })
            babyOneOffs.start()
        })
    }
}
